import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { billingService } from '@/services';
import { billingKeys } from '@/services/query-keys/billing-keys';
import { useToastStore } from '@/stores/toast-store';
import type { BillingExportJob } from '@/types';
import { getErrorMessage } from '@/utils/get-error-message';
import { useBlobDownload } from './use-blob-download';

/** How often a running export is asked where it is. */
const POLL_INTERVAL_MS = 1000;

/**
 * Failed polls in a row before the page gives up on an export. A minute of
 * them: a stand redeploying answers 502 for a while, and the job keeps running
 * on the server meanwhile.
 */
const POLL_RETRIES = 60;

const isActive = (job: BillingExportJob | undefined) =>
  job?.status === 'queued' || job?.status === 'running';

/** An answer no amount of asking again will change. */
const isFinalError = (error: unknown) =>
  error instanceof AxiosError &&
  [401, 403, 404].includes(error.response?.status ?? 0);

interface TrackedExport<T> {
  id: number;
  fallbackName: string;
  target: T;
}

/**
 * Background export core (LR-728), shared by the statement and the MTD preview
 * downloads.
 *
 * A large counterparty's document cannot be built inside the request timeout,
 * and a download that outran it used to arrive as a truncated file under a 200.
 * So the page never downloads directly any more: `start` asks the server to
 * build the file (POST on the export URL), the job is polled until it ends, and
 * a `done` job's file goes through the same blob -> save path as before.
 *
 * `target` names the document being exported, so a caller that keeps this hook
 * across documents - the preview's counterparty picker, whose modal is not
 * keyed - shows the progress only on the one it belongs to. The statement page
 * is not such a caller: its route remounts per statementId.
 *
 * Polling lives with the component: closing the dialog or leaving the page
 * stops it, while the job keeps running on the server. Asking for the same file
 * again while it runs joins that job (the server hands it back) rather than
 * starting another.
 *
 * NOTE: known ceiling - one export per hook instance, and nothing survives
 * closing the dialog, opening another document or reloading the page: a file
 * finished meanwhile is built again on the next click. A page-wide export tray
 * (a store over the user's jobs) is the upgrade path if traders need that.
 */
export const useBackgroundExport = <T>() => {
  const queryClient = useQueryClient();
  const { run, isDownloading, downloadError } = useBlobDownload();
  const addToast = useToastStore((s) => s.addToast);
  const [tracked, setTracked] = useState<TrackedExport<T> | null>(null);
  // The export whose finished file is being fetched. `tracked` is cleared the
  // moment the job is done - that is what keeps the save from being started
  // twice when the poll answers again - but the fetch is the slow half for
  // exactly the documents this feature exists for, and until it lands the
  // export still belongs to ITS document: otherwise the menu of whatever the
  // trader switched to wears the progress, and the file that arrives is not the
  // one it claims.
  const [saving, setSaving] = useState<TrackedExport<T> | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const { data: job, error: pollError } = useQuery({
    queryKey: billingKeys.exportJob(tracked?.id ?? 0),
    queryFn: ({ queryKey }) => billingService.getExport(queryKey[2]),
    enabled: tracked !== null,
    // The seeded answer is already out of date; ask at once.
    staleTime: 0,
    refetchInterval: (query) =>
      isActive(query.state.data) ? POLL_INTERVAL_MS : false,
    // A long export finishes while the trader is in another tab; the file
    // should still arrive then, not when they come back.
    refetchIntervalInBackground: true,
    retry: (failures, error) => failures < POLL_RETRIES && !isFinalError(error),
    retryDelay: POLL_INTERVAL_MS,
  });

  const fail = useCallback(
    (message: string) => {
      setTracked(null);
      setExportError(message);
      addToast('error', message);
    },
    [addToast]
  );

  useEffect(() => {
    if (!tracked || job?.id !== tracked.id) return;
    if (job.status === 'done') {
      setTracked(null);
      setSaving(tracked);
      void run(
        () => billingService.downloadExport(job.id),
        tracked.fallbackName
      ).finally(() => setSaving(null));
    } else if (job.status === 'failed') {
      fail(job.error || 'The export failed');
    } else if (!isActive(job)) {
      // Not one of the four statuses this page knows. Polling has stopped, so
      // saying so is the difference between an error and a button stuck on
      // "Preparing..." that no further export can be started from.
      fail('The export ended unexpectedly. Start it again.');
    }
  }, [job, tracked, run, fail]);

  useEffect(() => {
    if (!tracked || !pollError) return;
    fail(
      pollError instanceof AxiosError && pollError.response?.status === 404
        ? 'The export is no longer available. Start it again.'
        : getErrorMessage(pollError, 'Lost track of the export')
    );
  }, [pollError, tracked, fail]);

  const start = useCallback(
    async (
      begin: () => Promise<BillingExportJob>,
      fallbackName: string,
      target: T
    ) => {
      setIsStarting(true);
      setExportError(null);
      try {
        const started = await begin();
        // Seeded, so a joined job shows where it already is before the first poll.
        queryClient.setQueryData(billingKeys.exportJob(started.id), started);
        setTracked({ id: started.id, fallbackName, target });
      } catch (err) {
        // A refused request (an unsupported format, a bad counterparty) is JSON,
        // not a blob, so the plain unwrap reads it.
        const message = getErrorMessage(err, 'Failed to start the export');
        setExportError(message);
        addToast('error', message);
      } finally {
        setIsStarting(false);
      }
    },
    [addToast, queryClient]
  );

  return {
    start,
    isExporting: isStarting || tracked !== null || isDownloading,
    /** The export being built, or null while none is (or its file is being fetched). */
    job: tracked ? (job ?? null) : null,
    /** What `start` was told the running export is of - until its file is saved. */
    target: (tracked ?? saving)?.target ?? null,
    exportError: exportError ?? downloadError,
  };
};
